import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { UniversalLoadingState, Card, Button, NotificationCard, StatusBadge, UniversalPage } from '@sighthost/ui';
import {
  adminFinancialsService,
  type MonthlyDatum,
} from '@/domains/financials/services/adminFinancialsService';
import { exportRowsAsCsv, todayStamp } from '@/shared/utils/csvExport';
import { formatZAR, formatZARShort } from './components';

function ProjectionsPageBody() {
  const [data, setData] = useState<{
    history: MonthlyDatum[];
    projection: MonthlyDatum[];
    forecast_year_end: { revenue: number; expense: number; net: number };
    method: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminFinancialsService.getProjections().then(setData).catch((e: unknown) => {
      setError(e instanceof Error ? e.message : String(e));
    });
  }, []);

  if (error) return <NotificationCard tone="danger" title="Could not load projections" body={error} />;
  if (!data) return <UniversalLoadingState variant="spinner" caption="Computing projection…" />;

  const projTotals = data.projection.reduce(
    (acc, m) => ({ rev: acc.rev + m.revenue.total, exp: acc.exp + m.expense.total, net: acc.net + m.net }),
    { rev: 0, exp: 0, net: 0 },
  );

  const allMonths = [...data.history, ...data.projection];
  const maxRev = Math.max(...allMonths.map((m) => m.revenue.total), 1);

  function exportCsv() {
    const header = [
      'Month', 'Type', 'Hosting', 'Domains', 'Other', 'Revenue',
      'WHM hosting', 'domains.co.za top-up', 'PayFast fees',
      'Internet', 'Infrastructure', 'Tooling', 'Salary',
      'Marketing', 'Other expenses', 'Total expenses', 'Net',
    ];
    const allRows = [
      ...data!.history.map((m) => [
        m.month_label, 'actual', m.revenue.hosting, m.revenue.domains, m.revenue.other, m.revenue.total,
        m.expense.whm_hosting, m.expense.domains_topup, m.expense.payfast_fees,
        m.expense.internet, m.expense.infrastructure, m.expense.tooling,
        m.expense.salary, m.expense.marketing, m.expense.other, m.expense.total, m.net,
      ]),
      ...data!.projection.map((m) => [
        m.month_label, 'projected', m.revenue.hosting, m.revenue.domains, m.revenue.other, m.revenue.total,
        m.expense.whm_hosting, m.expense.domains_topup, m.expense.payfast_fees,
        m.expense.internet, m.expense.infrastructure, m.expense.tooling,
        m.expense.salary, m.expense.marketing, m.expense.other, m.expense.total, m.net,
      ]),
    ];
    exportRowsAsCsv(`financial-projections-${todayStamp()}.csv`, header, allRows);
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={exportCsv}>
          <Download className="h-4 w-4" /> Export CSV (24 months)
        </Button>
      </div>

      <Card interactive={false} className="border-warning/30 bg-warning/5 p-4 text-sm">
        <p>
          <strong className="text-foreground">Projection method.</strong> {data.method}
        </p>
      </Card>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card interactive={false} className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Year-end forecast revenue</p>
          <p className="mt-1 text-2xl font-semibold text-success">{formatZAR(data.forecast_year_end.revenue)}</p>
          <p className="mt-1 text-xs text-muted-foreground">YTD actual + remaining months projected</p>
        </Card>
        <Card interactive={false} className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Year-end forecast expense</p>
          <p className="mt-1 text-2xl font-semibold text-danger">{formatZAR(data.forecast_year_end.expense)}</p>
          <p className="mt-1 text-xs text-muted-foreground">YTD actual + monthly average projected</p>
        </Card>
        <Card interactive={false} className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Year-end forecast net</p>
          <p className={`mt-1 text-2xl font-semibold ${data.forecast_year_end.net >= 0 ? 'text-success' : 'text-danger'}`}>
            {formatZAR(data.forecast_year_end.net)}
          </p>
        </Card>
      </div>

      <Card interactive={false}>
        <header className="border-b border-border p-4">
          <h3 className="text-sm font-semibold text-foreground">Revenue: actual vs projected</h3>
          <p className="text-xs text-muted-foreground">
            Last 12 months (solid) followed by next 12 months (faded). Bar height ∝ revenue.
          </p>
        </header>
        <div className="grid grid-cols-[repeat(24,1fr)] gap-1 px-4 pt-4 pb-2">
          {allMonths.map((m, i) => {
            const isProj = i >= data.history.length;
            const h = (m.revenue.total / maxRev) * 100;
            return (
              <div
                key={m.month_start}
                className="flex flex-col items-center justify-end"
                title={`${m.month_label}: ${formatZAR(m.revenue.total)}${isProj ? ' (projected)' : ''}`}
              >
                <div className="flex h-32 w-full items-end">
                  <div
                    className={`w-full rounded-t transition-all ${isProj ? 'bg-success/30 border border-dashed border-success/50' : 'bg-success/70'}`}
                    style={{ height: `${Math.max(h, 1)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div className="px-4 pb-3 text-[10px] text-muted-foreground flex justify-between">
          <span>{data.history[0]?.month_label.split(' ')[0]}</span>
          <span className="font-medium">today</span>
          <span>{data.projection[data.projection.length - 1]?.month_label.split(' ')[0]}</span>
        </div>
      </Card>

      <Card interactive={false}>
        <header className="border-b border-border p-4">
          <h3 className="text-sm font-semibold text-foreground">Projected next 12 months (month-by-month)</h3>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-card-subtle text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">Month</th>
                <th className="px-4 py-2 text-right">Hosting</th>
                <th className="px-4 py-2 text-right">Domains</th>
                <th className="px-4 py-2 text-right">Other</th>
                <th className="px-4 py-2 text-right border-l border-border">Revenue</th>
                <th className="px-4 py-2 text-right">Expenses</th>
                <th className="px-4 py-2 text-right border-l border-border">Net</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.projection.map((m) => (
                <tr key={m.month_start}>
                  <td className="px-4 py-2 text-foreground">
                    {m.month_label} <StatusBadge tone="info">projected</StatusBadge>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatZARShort(m.revenue.hosting)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatZARShort(m.revenue.domains)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatZARShort(m.revenue.other)}</td>
                  <td className="px-4 py-2 text-right font-medium text-success tabular-nums border-l border-border">
                    {formatZARShort(m.revenue.total)}
                  </td>
                  <td className="px-4 py-2 text-right text-danger tabular-nums">{formatZARShort(m.expense.total)}</td>
                  <td className={`px-4 py-2 text-right font-semibold tabular-nums border-l border-border ${m.net >= 0 ? 'text-success' : 'text-danger'}`}>
                    {formatZARShort(m.net)}
                  </td>
                </tr>
              ))}
              <tr className="bg-card-subtle font-semibold">
                <td className="px-4 py-2">Next-12-month total</td>
                <td colSpan={3} />
                <td className="px-4 py-2 text-right text-success tabular-nums border-l border-border">{formatZARShort(projTotals.rev)}</td>
                <td className="px-4 py-2 text-right text-danger tabular-nums">{formatZARShort(projTotals.exp)}</td>
                <td className={`px-4 py-2 text-right tabular-nums border-l border-border ${projTotals.net >= 0 ? 'text-success' : 'text-danger'}`}>
                  {formatZARShort(projTotals.net)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/**
 * These five pages previously rendered NO page header at all — no title, no
 * breadcrumbs — because FinancialReportsLayout supplied a nav row and nothing
 * else. That was a §4.4 violation hiding inside a §4.1 one, and it is why no
 * financial report had a back link.
 */
export default function ProjectionsPage() {
  return (
    <UniversalPage
      title="Projections"
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Financials', href: '/admin/financials' },
        { label: 'Projections' },
      ]}
    >
      <ProjectionsPageBody />
    </UniversalPage>
  );
}
